import { BookingVisibilityEnum, ResourceTypeDtoMetaTypeEnum } from '@jooxter/api';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z, ZodRawShape } from 'zod';
import { useFetchUser } from '../../../queries';
import { IBookingFormDefaultValues, TResourceConfiguration } from '../types/booking-form.types';
import { useCreateOptionsDef } from '../hooks/useCreateOptionsDef';
import { createDateDef, dateTimeSchema } from '@jooxter/utils';
import { IBookingShapeParams, IPhysicalAttendeesDefOptions } from './types';

// These definitions needs to be set for properties when we know
// we will need to get default value from schema later
const createRruleDef = (defaultRrule?: string | null) =>
  defaultRrule ? z.string().optional().nullable().default(defaultRrule) : z.string().optional().nullable();

const createGuestsDef = (): ZodRawShape => ({
  internalAttendees: z.array(z.number()).min(0),
  externalAttendees: z.array(z.number()).min(0),
});

const createPhysicalAttendeesDef = (
  { t, minPhysicalAttendees, maxPhysicalAttendees }: IBookingShapeParams,
  { required, maxMessageKey }: IPhysicalAttendeesDefOptions = {}
) =>
  z
    .number(required ? { required_error: t<string>('booking-form.error.physical-attendees.required') } : undefined)
    .int(t<string>('booking-form.error.physical-attendees.pattern'))
    .nonnegative(t<string>('booking-form.error.physical-attendees.negative'))
    .min(minPhysicalAttendees, t<string>('booking-form.error.physical-attendees.min'))
    .max(maxPhysicalAttendees, t<string>(maxMessageKey ?? 'booking-form.error.physical-attendees.max'));

/**
 * The fields a resource adds on top of the base booking shape. Kept a pure function of its
 * arguments - and out of the hook body - so the memo that calls it can declare honest dependencies
 * instead of capturing objects rebuilt on every render.
 */
const createAdditionalShape = (params: IBookingShapeParams, config?: TResourceConfiguration): ZodRawShape => {
  const rrule = createRruleDef(params.defaultRrule);

  if (config?.isZone) {
    return {
      physicalAttendees: createPhysicalAttendeesDef(params, {
        required: true,
        maxMessageKey: 'booking-form.error.no-seat-remaining',
      }),
      rrule,
      internalAttendees: z
        .array(z.number())
        .min(params.minPhysicalAttendees, params.t<string>('booking-form.error.internal-attendees.min')),
      externalAttendees: z.array(z.number()).min(0),
    };
  }

  let shape: ZodRawShape;

  switch (config?.metaType) {
    case ResourceTypeDtoMetaTypeEnum.Desk:
    case ResourceTypeDtoMetaTypeEnum.Parking:
      shape = { rrule };
      break;
    case ResourceTypeDtoMetaTypeEnum.Vehicle:
    case ResourceTypeDtoMetaTypeEnum.Meeting:
    case ResourceTypeDtoMetaTypeEnum.Booth:
    case ResourceTypeDtoMetaTypeEnum.Bubble:
      shape = {
        physicalAttendees: createPhysicalAttendeesDef(params).optional().nullable(),
        rrule,
        ...createGuestsDef(),
      };
      break;
    case ResourceTypeDtoMetaTypeEnum.Visio:
    case ResourceTypeDtoMetaTypeEnum.Other:
      shape = { rrule, ...createGuestsDef() };
      break;
    default:
      shape = {};
      break;
  }

  if (config?.physicalAttendeesRequired) {
    shape = {
      ...shape,
      physicalAttendees: createPhysicalAttendeesDef(params, { required: true }).default(params.minPhysicalAttendees),
    };
  }

  if (config?.guestsRequired) {
    shape = { ...shape, ...createGuestsDef() };
  }

  return shape;
};

export const useBookingSchema = (
  minPhysicalAttendees: number,
  maxPhysicalAttendees: number,
  config?: TResourceConfiguration,
  defaultValues?: IBookingFormDefaultValues
) => {
  const { t } = useTranslation();
  const { user } = useFetchUser();
  const { createOptionsDef } = useCreateOptionsDef(config, defaultValues);

  const baseBookingSchema = useMemo(() => {
    let baseShape: {
      description: z.ZodNullable<z.ZodOptional<z.ZodString>>;
      resource: z.ZodObject<ZodRawShape>;
      start: z.ZodObject<{ date: typeof dateTimeSchema; time: z.ZodString }>;
      end: z.ZodObject<{ date: typeof dateTimeSchema; time: z.ZodString }>;
      options?: z.ZodObject<ZodRawShape>;
    } = {
      description: z.string().optional().nullable(),
      resource: z.object({
        id: z.number(),
      }),
      start: createDateDef(),
      end: createDateDef(),
    };

    const options = createOptionsDef();

    if (options) {
      baseShape = {
        ...baseShape,
        options,
      };
    }
    const simplestBookingSchema = z.object(baseShape);

    if (!user?.id || !defaultValues) {
      return simplestBookingSchema;
    }

    return simplestBookingSchema.extend({
      summary: z.string().min(1, t<string>('booking-form.error.summary.required')).default(defaultValues.summary),
      organizerId: z.number().default(user?.id),
      color: z.string().default(defaultValues.color),
      visibility: z.nativeEnum(BookingVisibilityEnum).default(defaultValues.visibility),
    });
  }, [user, defaultValues, createOptionsDef, t]);

  const schema = useMemo(() => {
    const additionalShape = createAdditionalShape(
      { t, minPhysicalAttendees, maxPhysicalAttendees, defaultRrule: defaultValues?.rrule },
      config
    );

    if (!config?.recurrence) {
      return baseBookingSchema.extend(additionalShape).omit({ rrule: true });
    }

    return baseBookingSchema.extend(additionalShape);
  }, [baseBookingSchema, config, minPhysicalAttendees, maxPhysicalAttendees, defaultValues?.rrule, t]);

  return schema;
};
